import { navigateMediaItem } from '@/helpers/navigateMediaItem';
import { closeModal } from '@/helpers/closeModal';
import { commonChangeMediaProcess, clearImageTimer, isMediaImageReady } from '@/helpers/mediaUtils';
import { updateCtaButtonAction } from '@/modules/actions/mobile/updateCtaButtonAction';
import { animateCurrentProgressBar, animateCurrentProgressBarInElement, prepareCurrentProgressBar, prepareCurrentProgressBarInElement } from '@/modules/actions/headerLineDuration';
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { mobileAutoPlayNextAction } from '@/modules/actions/mobile/mobileAutoPlayNextAction';
import { checkVolumeButtonState } from "@/helpers/volumeControl";
import pauseImg from '@/assets/images/pause-fill.svg';
import { removeStoreeeOverlay } from "@/helpers/removeOverlay";
import { isCtaHostTarget, queryCtaElement } from "@/helpers/ctaShadow";
import { isCampaignLoopPlaybackEnabled } from '@/helpers/campaignSettings';
import {
  maybeShowTargetingQuestionnaireForCurrentContent,
  maybeShowTargetingQuestionnaireForNextContent,
} from '@/helpers/maybeShowTargetingQuestionnaire';

export const changeMovieTapAction = () => {
  // cubeエフェクトの遷移中（swiper.animating）に連続タップすると、
  // active判定や座標判定がズレて「戻る/効かない」ように見えることがある。
  // 1タップ=1移動になるよう、遷移中のタップはキューに積んで遷移完了後に順次処理する。
  let queuedTapDelta = 0; // +1: next, -1: prev
  let isFlushingQueuedTaps = false;
  const isModalAlive = () => Boolean(document.querySelector('.storeeel-modal'));

  const flushQueuedTaps = () => {
    const swiper = getSwiperInstance();
    if (!swiper) {
      if (!isModalAlive()) {
        queuedTapDelta = 0;
        isFlushingQueuedTaps = false;
        return;
      }
      window.requestAnimationFrame(flushQueuedTaps);
      return;
    }

    if (swiper.animating) {
      window.requestAnimationFrame(flushQueuedTaps);
      return;
    }

    if (queuedTapDelta === 0) {
      isFlushingQueuedTaps = false;
      return;
    }

    const delta = queuedTapDelta > 0 ? 1 : -1;
    queuedTapDelta -= delta;

    const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
    if (!activeWrapper) {
      // Swiper(loop/cube) で active 判定が一瞬外れることがあるため、ここでは捨てずに待つ
      queuedTapDelta += delta;
      if (!isModalAlive()) {
        queuedTapDelta = 0;
        isFlushingQueuedTaps = false;
        return;
      }
      window.requestAnimationFrame(flushQueuedTaps);
      return;
    }

    executeTap(delta > 0, activeWrapper);
    window.requestAnimationFrame(flushQueuedTaps);
  };

  const enqueueTap = (delta) => {
    queuedTapDelta += delta;
    if (isFlushingQueuedTaps) return;
    isFlushingQueuedTaps = true;
    window.requestAnimationFrame(flushQueuedTaps);
  };

  const cancelPendingMediaPlayback = (wrapper) => {
    if (!wrapper) return;
    // タップ優先のため、読み込み中に復帰する自動再生/進捗ハンドラを解除する
    wrapper.querySelectorAll('video').forEach((video) => {
      try { video.pause(); } catch { void 0; }
      video.autoplay = false;
      video.removeAttribute('autoplay');

      if (video._storeeelThumbFallbackTimerId != null) {
        clearTimeout(video._storeeelThumbFallbackTimerId);
        video._storeeelThumbFallbackTimerId = null;
      }
      if (video._storeeelThumbOnReady) {
        video.removeEventListener('loadedmetadata', video._storeeelThumbOnReady);
        video.removeEventListener('loadeddata', video._storeeelThumbOnReady);
        video.removeEventListener('canplay', video._storeeelThumbOnReady);
        video._storeeelThumbOnReady = null;
      }
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      if (video._onCanPlayForProgress) {
        video.removeEventListener('canplay', video._onCanPlayForProgress);
        video._onCanPlayForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }
      if (video._endedListener) {
        video.removeEventListener('ended', video._endedListener);
        delete video._endedListener;
      }
      if (video.onloadeddata) {
        video.onloadeddata = null;
      }
    });

    wrapper.querySelectorAll('img.storeeel-media-image').forEach((image) => {
      if (image._onLoadForProgress) {
        image.removeEventListener('load', image._onLoadForProgress);
        image._onLoadForProgress = null;
      }
      if (image._onErrorForProgress) {
        image.removeEventListener('error', image._onErrorForProgress);
        image._onErrorForProgress = null;
      }
      if (image._onLoadForAutoPlay) {
        image.removeEventListener('load', image._onLoadForAutoPlay);
        image._onLoadForAutoPlay = null;
      }
      if (image._onErrorForAutoPlay) {
        image.removeEventListener('error', image._onErrorForAutoPlay);
        image._onErrorForAutoPlay = null;
      }
    });
  };

  // iOS（タッチ）環境では Swiper が click を抑止することがあり、
  // 連続タップ時に「タップが効かない」状態になるため touchend でも拾う。
  const tapMoveThresholdPx = 12;
  const tapMoveThresholdSq = tapMoveThresholdPx * tapMoveThresholdPx;
  const ignoreClickAfterTouchMs = 700;
  const ignoreTapIfHeldLongerThanMs = 450; // long-press(500ms)の直前でガード
  let lastTouchHandledAt = 0;

  const shouldIgnoreTapTarget = (target) => {
    if (!target || typeof target.closest !== 'function') return false;
    return Boolean(
      isCtaHostTarget(target)
      || target.closest('.storeeel-menu-area-icons')
      || target.closest('.storeee_overlay')
      // 広告バッジ/広告説明ポップオーバー上のタップは、前後移動タップとして扱わない
      || target.closest('.storeeel-ad-badge')
      || target.closest('.storeeel-ad-disclosure')
    );
  };

  const wrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .slide-media-wrapper');
  wrappers.forEach((wrapper) => {
    wrapper.addEventListener('click', (e) => {
      if (Date.now() - lastTouchHandledAt < ignoreClickAfterTouchMs) return;
      if (shouldIgnoreTapTarget(e.target)) return;
      e.__storeeelMediaTapHandled = true;
      // cubeエフェクト＋loop時は「非アクティブスライド」をタップしてしまうことがあり、
      // その場合に意図しない方向へ進んだり、タップが効かないように見えることがある。
      // どのスライドをタップしても、常にアクティブスライドを基準に処理する。
      const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
      moviePlayerController(e.clientX, activeWrapper || wrapper, true);
    });
  });

  // wrapper 直下に届かない（cube遷移中のtransform等）タップも拾えるよう、
  // モーダル全体で「タップ判定」してアクティブスライドへ送る。
  const modalRoot = document.querySelector('.storeeel-modal');
  if (modalRoot) {
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTimeStamp = 0;
    let touchMoved = false;

    modalRoot.addEventListener('touchstart', (e) => {
      if (e.touches?.length !== 1) {
        touchMoved = true;
        return;
      }
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      // Date.now() はメインスレッド負荷でズレるため、イベントの timeStamp を使う
      touchStartTimeStamp = e.timeStamp;
      touchMoved = false;
    }, { passive: true, capture: true });

    modalRoot.addEventListener('touchmove', (e) => {
      if (touchMoved) return;
      const touch = e.touches?.[0];
      if (!touch) return;
      const dx = touch.clientX - touchStartX;
      const dy = touch.clientY - touchStartY;
      if (dx * dx + dy * dy > tapMoveThresholdSq) {
        touchMoved = true;
      }
    }, { passive: true, capture: true });

    modalRoot.addEventListener('touchend', (e) => {
      if (touchMoved) return;
      if (e.timeStamp - touchStartTimeStamp >= ignoreTapIfHeldLongerThanMs) return;
      if (shouldIgnoreTapTarget(e.target)) return;

      const touch = e.changedTouches?.[0];
      if (!touch) return;

      const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
      lastTouchHandledAt = Date.now();
      if (!activeWrapper) {
        const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
        const isNext = viewportWidth ? touch.clientX > viewportWidth / 2 : true;
        enqueueTap(isNext ? 1 : -1);
        return;
      }

      moviePlayerController(touch.clientX, activeWrapper, true);
    }, { passive: true, capture: true });

    modalRoot.addEventListener('click', (e) => {
      if (e.__storeeelMediaTapHandled) return;
      if (Date.now() - lastTouchHandledAt < ignoreClickAfterTouchMs) return;
      if (shouldIgnoreTapTarget(e.target)) return;

      const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
      if (!activeWrapper) return;

      const rect = activeWrapper.getBoundingClientRect();
      const isInsideActiveWrapper =
        e.clientX >= rect.left
        && e.clientX <= rect.right
        && e.clientY >= rect.top
        && e.clientY <= rect.bottom;
      if (!isInsideActiveWrapper) return;

      // 進捗ヘッダーなど wrapper の上に重なる要素を押した場合も、表示面タップとして扱う
      e.__storeeelMediaTapHandled = true;
      moviePlayerController(e.clientX, activeWrapper, true);
    });
  }

  function executeTap(isNext, wrapper) {
    cancelPendingMediaPlayback(wrapper);
    const swiper = getSwiperInstance();
    if (!swiper) {
      enqueueTap(isNext ? 1 : -1);
      return;
    }
    let didGroupTransition = false;
    const normalizeGroupId = (value) => (value == null ? '' : String(value).split('-').pop());
    const prevGroupId = normalizeGroupId(window.iconId);
    const isCircle = window.storeeelItems?.frame === 'circle';

    // すぐにCTAボタンを非表示にする(チラつき防止)。
    // ただし「同一グループ内の次/前コンテンツへの切替」かつ「切替先にも CTA テキストがある」場合は、
    // ここで隠すと逆に CTA が一瞬消える点滅になるため隠さない（updateCtaButtonAction が
    // テキスト/位置を更新して表示を維持する）。グループ遷移時は cube ちらつき防止のため従来どおり隠す。
    const ctaButton = queryCtaElement('.ctaFrame', wrapper);
    const ctaGroupsForTap = window.storeeelItems?.contentGroups || [];
    const currentGroupForCta = ctaGroupsForTap.find((g) => normalizeGroupId(g?.id) === prevGroupId);
    const currentPosForCta = Number(window.moviePosition || 0);
    const nextPosForCta = isNext ? currentPosForCta + 1 : currentPosForCta - 1;
    const staysInGroupForCta = !!currentGroupForCta
      && nextPosForCta >= 0
      && nextPosForCta < (currentGroupForCta.contents?.length || 0);
    const nextContentForCta = staysInGroupForCta ? currentGroupForCta.contents[nextPosForCta] : null;
    const nextHasCtaText = (nextContentForCta?.ctaText || '').trim() !== '';
    if (ctaButton && !(staysInGroupForCta && nextHasCtaText)) {
      ctaButton.style.visibility = 'hidden';
    }

    // 画像タイマーのクリア
    clearImageTimer();

    if (isNext) {
      if (isCircle && maybeShowTargetingQuestionnaireForNextContent({
        onContinueWithoutAnswers: () => {
          executeTap(true, wrapper);
        },
      })) {
        return;
      }
      // 次の動画を再生
      window.moviePosition += 1;
      const contents = navigateMediaItem();
      if (!contents) {
        const overlay = document.querySelector('.storeee_overlay');
        if (overlay) overlay.remove();
        const overlayStyle = document.querySelector('style[data-storeee-overlay]');
        if (overlayStyle) overlayStyle.remove();
        return closeModal();
      }
      const nextGroupId = normalizeGroupId(window.iconId);
      const didGroupChange = prevGroupId && nextGroupId ? prevGroupId !== nextGroupId : true;
      // 単一グループのループではスライド遷移しないため、同一グループは通常更新する
      const shouldHandleGroupTransition = !isCircle ? (window.moviePosition == 0) : (window.moviePosition == 0 && didGroupChange);

      if (!shouldHandleGroupTransition) {
        window._storeeelPendingGroupSlideIndex = null;
        commonChangeMediaProcess(contents);
      } else {
        // グループ移動は transitionEnd 側で進捗開始するため、ここでは進捗処理を行わない
        didGroupTransition = true;
        swiper.slideNext();
      }
    } else {
      // 前の動画を再生
      window.moviePosition -= 1;
      const tmpInt = window.moviePosition;
      let contents = null;

      // ストーリー型の「先頭で左タップ」は、active DOM と window.iconId が入れ替わる瞬間があり、
      // navigateMediaItem に全面委譲すると誤ってモーダルを閉じることがあるため境界だけ先に確定する。
      if (isCircle && tmpInt === -1) {
        const groups = window.storeeelItems?.contentGroups || [];
        const currentGroupId =
          normalizeGroupId(wrapper?.getAttribute('group-id'))
          || normalizeGroupId(window.iconId);
        const currentGroupIndex = groups.findIndex(group => String(group?.id) === currentGroupId);
        const loopPlaybackEnabled = isCampaignLoopPlaybackEnabled(window.storeeelItems);

        if (currentGroupIndex !== -1) {
          const targetGroupIndex =
            currentGroupIndex > 0
              ? currentGroupIndex - 1
              : (loopPlaybackEnabled ? groups.length - 1 : -1);
          const targetGroup = targetGroupIndex >= 0 ? groups[targetGroupIndex] : null;

          if (targetGroup) {
            window._storeeelPendingGroupSlideIndex = targetGroupIndex;
            window.iconId = String(targetGroup.id);
            // 前グループへ戻る時は常に先頭コンテンツから再生する。
            window.moviePosition = 0;
            contents = targetGroup.contents;
          }
        }
      }

      if (!contents) {
        contents = navigateMediaItem();
      }

      if (!contents) {
        // モーダルが閉じた時にオーバーレイも消えるように追加
        removeStoreeeOverlay();
        return closeModal();
      }

      const nextGroupId = normalizeGroupId(window.iconId);
      const didGroupChange = prevGroupId && nextGroupId ? prevGroupId !== nextGroupId : true;
      const shouldHandleGroupTransition = !isCircle ? (tmpInt == -1) : (tmpInt == -1 && didGroupChange);

      if (shouldHandleGroupTransition) {
        if (isCircle) {
          // ストーリー型のグループ戻りは、スワイプ時と同様に先頭コンテンツから再生する。
          window.moviePosition = 0;
        }
        // 最初の動画に戻った場合
        const pendingIdx = Number.isFinite(window._storeeelPendingGroupSlideIndex)
          ? window._storeeelPendingGroupSlideIndex
          : null;
        window._storeeelPendingGroupSlideIndex = null;
        didGroupTransition = true;
        if (isCircle && typeof swiper?.slidePrev === 'function') {
          // ストーリー型の前グループ遷移は、次グループと同じく 1 ステップ移動に揃える。
          // cube + initialSlide>0 で slideTo を使うと、active スライドの確定が崩れることがある。
          swiper.slidePrev();
        } else if (pendingIdx != null && typeof swiper?.slideToLoop === 'function') {
          swiper.slideToLoop(pendingIdx);
        } else if (pendingIdx != null && typeof swiper?.slideTo === 'function') {
          swiper.slideTo(pendingIdx);
        } else {
          swiper.slidePrev();
        }
      } else {
        window._storeeelPendingGroupSlideIndex = null;
        commonChangeMediaProcess(contents);
      }
    }

    if (didGroupTransition) {
      // グループ移動は groupSwipeAction 側で進捗/自動再生/CTA を更新する
      return;
    }

    // 再生ボタンの状態を必ず停止ボタンにする
    const playButton = document.querySelector('.storeeel-menu-area-icons #start-desktop');
    if (playButton) {
      playButton.src = pauseImg;
      playButton.alt = 'pause';
      playButton.id = 'pause-desktop';
    }

    // 自動再生の設定
    mobileAutoPlayNextAction();

    // 音量ボタンの状態に応じて動画のミュート変更
    checkVolumeButtonState();

    // 進捗バーのためのインデックスを計算
    let groupIndex = window.storeeelItems.contentGroups.findIndex(group => group.id === window.iconId);
    let progressIndex = 0;
    if (window.storeeelItems.frame === "circle") {
      progressIndex = window.moviePosition;
    } else if (groupIndex == 0) {
      progressIndex = window.moviePosition;
    } else {
      for (let i = 0; i < groupIndex; i++) {
        progressIndex += window.storeeelItems.contentGroups[i].contents.length;
      }
      progressIndex += window.moviePosition;
    }
    // 進捗バーのアニメーションを開始
    const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
    const activeWrapper = activeSlide?.querySelector('.slide-media-wrapper');
    showMediaLoadingShimmer(activeWrapper);
    const useElementProgress = window.storeeelItems.frame === "circle" && activeSlide;
    const prepareProgress = () => {
      if (useElementProgress) {
        prepareCurrentProgressBarInElement(progressIndex, activeSlide);
      } else {
        prepareCurrentProgressBar(progressIndex);
      }
    };
    const video = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper video.storeeel-movie');
    if (video && video.style.display !== 'none') {
      // 既存のloadedmetadataハンドラがあれば解除
      if (video._onLoadedMetadataForProgress) {
        video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
        video._onLoadedMetadataForProgress = null;
      }
      // 既存のcanplayハンドラがあれば解除
      if (video._onCanPlayForProgress) {
        video.removeEventListener('canplay', video._onCanPlayForProgress);
        video._onCanPlayForProgress = null;
      }
      if (video._onPlayingForProgress) {
        video.removeEventListener('playing', video._onPlayingForProgress);
        video._onPlayingForProgress = null;
      }

      const cleanup = () => {
        if (video._onLoadedMetadataForProgress) {
          video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video._onLoadedMetadataForProgress = null;
        }
        if (video._onCanPlayForProgress) {
          video.removeEventListener('canplay', video._onCanPlayForProgress);
          video._onCanPlayForProgress = null;
        }
        if (video._onPlayingForProgress) {
          video.removeEventListener('playing', video._onPlayingForProgress);
          video._onPlayingForProgress = null;
        }
      };

      const startProgress = (duration) => {
        const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
        if (window.storeeelItems.frame === "circle" && currentSlide) {
          animateCurrentProgressBarInElement(duration, progressIndex, currentSlide);
        } else {
          animateCurrentProgressBar(duration, progressIndex);
        }
        hideMediaLoadingShimmer(activeWrapper);
      };

      const tryStartProgress = () => {
        const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
        if (!slide || slide !== activeSlide || !slide.contains(video)) {
          cleanup();
          hideMediaLoadingShimmer(activeWrapper);
          return false;
        }
        if (video.paused || video.ended) return false;
        if (video.readyState < 2) return false;
        const duration = Number(video.duration);
        if (!Number.isFinite(duration) || duration <= 0) return false;
        startProgress(duration);
        return true;
      };

      const onReady = () => {
        if (tryStartProgress()) {
          cleanup();
        }
      };

      // 再生開始まで進捗を開始しない
      prepareProgress();
      video._onLoadedMetadataForProgress = onReady;
      video._onPlayingForProgress = onReady;
      video.addEventListener('loadedmetadata', onReady);
      video.addEventListener('playing', onReady);
      onReady();
    } else {
      const image = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper img.storeeel-media-image');
      if (image) {
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }

        const cleanup = () => {
          if (image._onLoadForProgress) {
            image.removeEventListener('load', image._onLoadForProgress);
            image._onLoadForProgress = null;
          }
          if (image._onErrorForProgress) {
            image.removeEventListener('error', image._onErrorForProgress);
            image._onErrorForProgress = null;
          }
        };

        const startProgress = () => {
          const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          if (!currentSlide || currentSlide !== activeSlide || !currentSlide.contains(image)) {
            cleanup();
            hideMediaLoadingShimmer(activeWrapper);
            return;
          }
          if (window.storeeelItems.frame === "circle" && currentSlide) {
            animateCurrentProgressBarInElement(5, progressIndex, currentSlide);
          } else {
            animateCurrentProgressBar(5, progressIndex);
          }
          hideMediaLoadingShimmer(activeWrapper);
          cleanup();
        };

        const tryStartProgress = () => {
          const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          if (!currentSlide || currentSlide !== activeSlide || !currentSlide.contains(image)) {
            cleanup();
            hideMediaLoadingShimmer(activeWrapper);
            return false;
          }
          if (!isMediaImageReady(image)) return false;
          startProgress();
          return true;
        };

        // 読み込み完了後に進捗を開始する
        prepareProgress();
        image._onLoadForProgress = () => { tryStartProgress(); };
        image._onErrorForProgress = () => { cleanup(); };
        image.addEventListener('load', image._onLoadForProgress);
        image.addEventListener('error', image._onErrorForProgress);
        tryStartProgress();
      }
    }

    // CTAボタンの更新
    updateCtaButtonAction(wrapper)
    if (maybeShowTargetingQuestionnaireForCurrentContent()) return;
  }

  function moviePlayerController(clickX, wrapper, isClientX = false) {
    const rect = wrapper.getBoundingClientRect();
    // cubeエフェクト（3D transform）下では wrapper のサイズ/位置が見かけとズレやすい。
    // 「画面の左右どちらをタップしたか」で判定すると、ユーザー操作と同じ感覚で安定する。
    const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
    const movieWidth = rect.width || wrapper.clientWidth;
    const isNext =
      isClientX
        ? (viewportWidth ? clickX > viewportWidth / 2 : clickX - rect.left > movieWidth / 2)
        : clickX > movieWidth / 2;
    const swiper = getSwiperInstance();
    if (swiper?.animating) {
      enqueueTap(isNext ? 1 : -1);
      return;
    }

    executeTap(isNext, wrapper);
  }

  window.addEventListener('storeeel-custom-tap', (e) => {
    const { x, wrapper } = e.detail;
    moviePlayerController(x, wrapper, false);
  });
}
